import { useAuiState } from '@assistant-ui/react';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import { agentWorkbenchNotifyOnRunCompleteAtom } from '#state/atoms/agent-workbench-notify-on-run-complete-atom.js';
import { showRunCompleteNotification } from './run-complete-notification.js';
import { isTabInForeground } from './tab-foreground.js';

export interface UseNotifyOnRunCompleteOptions {
	/**
	 * Whether this surface may notify. Off for panels that offer no way to turn
	 * notifications back off, so a preference set elsewhere cannot make a
	 * read-only or hidden panel start announcing runs.
	 */
	enabled: boolean;
}

/**
 * Raises a browser notification when the active thread's run finishes while the
 * user is looking elsewhere, so a long run does not have to be babysat. Silent
 * while the tab is in the foreground, while the user has notifications off, and
 * on surfaces that cannot offer the toggle. Must be called inside an
 * `AssistantRuntimeProvider`.
 *
 * @param options.enabled - Whether this surface may notify.
 */
export function useNotifyOnRunComplete({ enabled }: UseNotifyOnRunCompleteOptions): void {
	const isPreferenceOn = useAtomValue(agentWorkbenchNotifyOnRunCompleteAtom);
	const isRunning = useAuiState((s) => s.thread.isRunning);
	const conversationId = useAuiState((s) => s.threadListItem.remoteId);
	const title = useAuiState((s) => s.threadListItem.title);

	// The notification identifies the conversation, but it must not re-arm the
	// transition below when a title arrives mid-run, so it is read through a ref.
	// Declared first so the sync lands before the notifying effect reads it.
	const labelRef = useRef(title);
	useEffect(() => {
		labelRef.current = title;
	});

	// Ref to track the last observed state of the active conversation.
	const lastObserved = useRef<{ conversationId: string | undefined; isRunning: boolean }>({
		conversationId,
		isRunning: false,
	});

	// Whenever the active conversation's run state changes, check if a notification should be shown.
	useEffect(() => {
		const previous = lastObserved.current;
		lastObserved.current = { conversationId, isRunning };

		const hasJustFinished = previous.conversationId === conversationId && previous.isRunning && !isRunning;
		if (!hasJustFinished || !enabled || !isPreferenceOn || isTabInForeground()) {
			return;
		}
		showRunCompleteNotification({
			conversationLabel: labelRef.current ?? 'Untitled conversation',
			conversationId,
		});
	}, [conversationId, isRunning, enabled, isPreferenceOn]);
}
